import { useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { PremiumFooter } from '../src/components/landing/PremiumFooter';
import { Container } from '../src/components/primitives/Container';
import { Text } from '../src/components/primitives/Text';
import { useTranslation } from '../src/contexts/TranslationContext';
import { colors } from '../src/theme/colors';
import { spacing } from '../src/theme/spacing';

const LAST_UPDATED = 'July 27, 2026';

interface SectionProps {
  title: string;
  children: ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <View style={styles.section}>
    <Text variant="h4" style={styles.sectionTitle}>
      {title}
    </Text>
    {children}
  </View>
);

const Paragraph = ({ children }: { children: ReactNode }) => (
  <Text variant="body" color={colors.text.secondary} style={styles.paragraph}>
    {children}
  </Text>
);

const BulletItem = ({ children }: { children: ReactNode }) => (
  <View style={styles.bulletRow}>
    <Text variant="body" color={colors.primary[500]} style={styles.bullet}>
      {'•'}
    </Text>
    <Text variant="body" color={colors.text.secondary} style={styles.bulletText}>
      {children}
    </Text>
  </View>
);

export default function TermsPage() {
  const router = useRouter();
  const { t } = useTranslation();

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Container maxWidth="md">
        <Pressable
          testID="terms-back-button"
          onPress={() => router.push('/landing')}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel={t('common.actions.goBack')}
        >
          <ArrowLeft size={20} color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.primary[500]}>
            {t('common.actions.goBack')}
          </Text>
        </Pressable>

        <View style={styles.header}>
          <Text testID="terms-heading" variant="h1" style={styles.title}>
            Terms of Service
          </Text>
          <Text variant="bodySmall" color={colors.text.tertiary}>
            Last updated: {LAST_UPDATED}
          </Text>
        </View>

        <View style={styles.divider} />

        <View testID="terms-content">
          <Section title="1. Agreement to Terms">
            <Paragraph>
              These Terms of Service ("Terms") govern your access to and use of Funnel Panel,
              including our website, mobile applications, and related services (collectively, the
              "Service"), operated by FLOWSOFT S.A.S ("we," "our," or "us"), with a registered
              address at Calle 64 #77-41, Urb Turmalina San Germán, Robledo, Medellín, Colombia.
            </Paragraph>
            <Paragraph>
              By creating an account, accessing, or otherwise using the Service, you agree to be
              bound by these Terms and by our Privacy Policy. If you are entering into these Terms
              on behalf of a company or other organization, you represent that you have authority to
              bind that organization, and "you" refers to that organization. If you do not agree to
              these Terms, do not use the Service.
            </Paragraph>
          </Section>

          <Section title="2. Payments">
            <Paragraph>
              Wompi processes authorized purchases as one-time prepaid access periods. Fees, taxes,
              the access period, and the refund process are shown at checkout; we do not charge
              without your authorization.
            </Paragraph>
          </Section>

          <Section title="3. Eligibility and Accounts">
            <Paragraph>
              You must be at least 16 years old and capable of forming a binding contract to use the
              Service. You are responsible for the accuracy of the information you provide, for
              safeguarding your account credentials, and for all activity that occurs under your
              account. Notify us immediately of any unauthorized use.
            </Paragraph>
            <Paragraph>
              Accounts belong to the organization associated with them. Where the Service assigns
              ownership of an organization based on a verified company email domain, you represent
              that you are authorized to act on behalf of that organization.
            </Paragraph>
          </Section>

          <Section title="4. Description of the Service">
            <Paragraph>
              Funnel Panel is a sales funnel, lead management, and outreach platform. Depending on
              your plan, it may include lead and contact management, funnel and pipeline tools, data
              enrichment, prospect search, multi-channel messaging (including email, SMS, WhatsApp,
              and voice), AI-assisted automation and agents, analytics, and related features.
            </Paragraph>
            <Paragraph>
              We may add, change, or remove features at any time. We will use reasonable efforts to
              notify you of material changes that adversely affect core functionality of a paid
              plan.
            </Paragraph>
          </Section>

          <Section title="5. Subscriptions, Fees, and Billing">
            <BulletItem>
              New Wompi paid access is purchased in advance for a one-time monthly or annual period.
              Plan limits and included allowances depend on the tier you select.
            </BulletItem>
            <BulletItem>
              Wompi charges the disclosed one-time fee and applicable taxes only after you authorize
              each checkout. Fees are stated exclusive of taxes unless indicated otherwise.
            </BulletItem>
            <BulletItem>
              New Wompi purchases do not create a recurring subscription and do not renew
              automatically.
            </BulletItem>
            <BulletItem>
              Legacy recurring subscriptions purchased before the current Wompi prepaid offering may
              continue to renew under the billing terms accepted at purchase until canceled. This
              legacy renewal behavior does not apply to new Wompi purchases.
            </BulletItem>
            <BulletItem>
              For billing, cancellation, or refund support, email contact@flowsoftdev.com; our
              Refund Policy is available at /refunds.
            </BulletItem>
            <BulletItem>
              Some features are metered and consume credits or allowances. Usage beyond your plan's
              included allowance may require an upgrade or may be subject to additional charges
              where stated.
            </BulletItem>
          </Section>

          <Section title="6. Acceptable Use">
            <Paragraph>You agree not to use the Service to:</Paragraph>
            <BulletItem>Violate any applicable law, regulation, or third-party right.</BulletItem>
            <BulletItem>
              Send unsolicited, unlawful, or deceptive communications (spam), or engage in any form
              of fraud.
            </BulletItem>
            <BulletItem>
              Infringe intellectual property, publicity, or privacy rights of others.
            </BulletItem>
            <BulletItem>
              Introduce malware, probe or breach security or authentication measures, scrape or
              access the Service by unauthorized means, or interfere with its operation.
            </BulletItem>
            <BulletItem>
              Resell, sublicense, or provide the Service to third parties except as expressly
              permitted.
            </BulletItem>
          </Section>

          <Section title="7. Your Data and Responsibilities">
            <Paragraph>
              You retain ownership of the data you and your organization submit to the Service,
              including lead and contact records ("Customer Data"). As between you and us, you are
              the controller of Customer Data and we act as your processor, handling it on your
              behalf to provide the Service.
            </Paragraph>
            <Paragraph>You are solely responsible for ensuring that:</Paragraph>
            <BulletItem>
              You have a valid lawful basis and any required consents to collect, upload, enrich,
              and process personal data of your leads and contacts, including data obtained from
              third-party or public sources.
            </BulletItem>
            <BulletItem>
              Your outreach complies with applicable marketing, anti-spam, telemarketing, call
              recording, and do-not-call laws in each relevant jurisdiction, and that you obtain any
              consents those laws require.
            </BulletItem>
            <BulletItem>
              You honor opt-out, do-not-call, and deletion requests from the individuals you
              contact.
            </BulletItem>
            <Paragraph>
              You will indemnify us against claims arising from Customer Data or your use of the
              Service that violates this Section or applicable law, as described in Section 12.
            </Paragraph>
          </Section>

          <Section title="8. AI Features and Automated Outreach">
            <Paragraph>
              The Service includes AI-assisted and automated features, such as AI voice agents,
              automated calling, enrichment, and content generation. These features may produce
              inaccurate or incomplete output; you are responsible for reviewing output before you
              rely on or act on it.
            </Paragraph>
            <Paragraph>
              Where you use automated calling, recording, or AI-driven communication, you are
              responsible for making any disclosures and obtaining any consents required by law,
              including disclosure that a call is recorded or that an interaction is automated or
              AI-generated.
            </Paragraph>
          </Section>

          <Section title="9. Intellectual Property">
            <Paragraph>
              The Service, including its software, design, branding, and documentation, is owned by
              us and our licensors and is protected by intellectual property laws. We grant you a
              limited, non-exclusive, non-transferable, revocable right to use the Service in
              accordance with these Terms. We reserve all rights not expressly granted.
            </Paragraph>
          </Section>

          <Section title="10. Third-Party Services">
            <Paragraph>
              The Service integrates with third-party providers (for example, for authentication,
              payments, telephony, messaging, enrichment, AI models, and analytics). Your use of
              those integrations may be subject to the third parties' own terms, and we are not
              responsible for third-party services. A current list of subprocessors is described in
              our Privacy Policy.
            </Paragraph>
          </Section>

          <Section title="11. Service Availability and Disclaimers">
            <Paragraph>
              We work to keep the Service available and reliable, but we do not guarantee that it
              will be uninterrupted, error-free, or secure, or that results obtained through it will
              be accurate. The Service is provided "as is" and "as available" without warranties of
              any kind, whether express or implied, including implied warranties of merchantability,
              fitness for a particular purpose, and non-infringement, to the maximum extent
              permitted by law.
            </Paragraph>
          </Section>

          <Section title="12. Limitation of Liability and Indemnification">
            <Paragraph>
              To the maximum extent permitted by law, we will not be liable for any indirect,
              incidental, special, consequential, or punitive damages, or for lost profits, revenue,
              data, or goodwill. Our total aggregate liability arising out of or relating to the
              Service will not exceed the amounts you paid for the Service in the twelve months
              preceding the event giving rise to the claim.
            </Paragraph>
            <Paragraph>
              You will indemnify and hold us harmless from any claims, damages, liabilities, and
              expenses (including reasonable legal fees) arising from your Customer Data, your use
              of the Service, or your breach of these Terms or applicable law.
            </Paragraph>
          </Section>

          <Section title="13. Suspension and Termination">
            <Paragraph>
              We may suspend or terminate your access to the Service if you materially breach these
              Terms, fail to pay fees when due, create a security or fraud risk, or repeatedly or
              seriously violate our policies or applicable law. You may stop using the Service and
              close your account at any time.
            </Paragraph>
            <Paragraph>
              Upon termination, your right to use the Service ends. We will handle your data
              following termination as described in our Privacy Policy, including any applicable
              export and deletion periods.
            </Paragraph>
          </Section>

          <Section title="14. Changes to These Terms">
            <Paragraph>
              We may update these Terms from time to time. We will post the updated Terms on this
              page and revise the "Last updated" date, and where changes are material we will
              provide reasonable notice. Your continued use of the Service after changes take effect
              constitutes acceptance of the revised Terms.
            </Paragraph>
          </Section>

          <Section title="15. Governing Law">
            <Paragraph>
              These Terms are governed by the laws of Colombia, without regard to conflict-of-law
              rules. The courts located in Colombia will have exclusive jurisdiction over any
              dispute arising out of or relating to these Terms or the Service, except where
              applicable mandatory consumer law provides otherwise.
            </Paragraph>
          </Section>

          <Section title="16. Contact Us">
            <Paragraph>
              If you have any questions about these Terms, please contact us at:
            </Paragraph>
            <BulletItem>Email: contact@flowsoftdev.com</BulletItem>
            <BulletItem>Company: FLOWSOFT S.A.S</BulletItem>
          </Section>
        </View>
      </Container>

      <PremiumFooter />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flexGrow: 1,
    paddingTop: Platform.select({ web: 60, default: 40 }),
    paddingBottom: spacing.xxl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xl,
    alignSelf: 'flex-start',
  },
  header: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  title: {
    fontSize: Platform.select({ web: 42, default: 32 }),
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.xxl,
  },
  section: {
    marginBottom: spacing.xxl,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: spacing.md,
    color: colors.text.primary,
  },
  paragraph: {
    lineHeight: Platform.select({ web: 26, default: 24 }),
    marginBottom: spacing.md,
  },
  bulletRow: {
    flexDirection: 'row',
    paddingLeft: spacing.md,
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  bullet: {
    lineHeight: Platform.select({ web: 26, default: 24 }),
  },
  bulletText: {
    flex: 1,
    lineHeight: Platform.select({ web: 26, default: 24 }),
  },
});
